import { useSyncExternalStore } from "react"
import { dispatchCart, type CartItem } from "@/components/editor/edit-cart"
import { reviewActions } from "./review-store"
import { getReviewPr } from "./review-pr"
import type { QueueItem } from "@/lib/review/queue"

/** A sign-off whose POST came back non-ok / failed — surfaced for retry. */
export interface SignoffError {
  key: string
  commit: PendingCommit["commit"]
}

/** How long an action stays undoable before its sign-off is committed. */
export const UNDO_WINDOW_MS = 5000

export interface PendingCommit {
  kind: "approve" | "edit"
  /** Message key — toast label. */
  key: string
  /** What `postSignoff` needs once the window closes. */
  commit: {
    item: QueueItem
    approvedValue: string
    pendingMerge: boolean
    idToken: string
  }
  /** Everything needed to roll the client back if undone. */
  revert: {
    item: QueueItem
    /** Cursor index the item occupied before it was completed. */
    index: number
    /** edit-&-approve only: the cart entry for this key before the edit (null = none → remove on undo). */
    priorCartItem: CartItem | null
  }
}

interface InternalPending extends PendingCommit {
  id: number
  createdAt: number
  timer: ReturnType<typeof setTimeout>
}

let stack: InternalPending[] = []
let nextId = 1
let lastError: SignoffError | null = null
const listeners = new Set<() => void>()
const errorListeners = new Set<() => void>()

function emit() {
  listeners.forEach((l) => l())
}
function emitError() {
  errorListeners.forEach((l) => l())
}

/** POSTs the sign-off. Resolves true on a 2xx, false on a non-ok / network fail
 *  — the caller surfaces a failure for retry rather than dropping it silently. */
async function postSignoff(c: PendingCommit["commit"]): Promise<boolean> {
  const res = await fetch("/api/review/signoff", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "User-Authorization": `Bearer ${c.idToken}`,
    },
    body: JSON.stringify({
      key: c.item.key,
      locale: c.item.locale,
      enValue: c.item.enValue,
      approvedValue: c.approvedValue,
      pendingMerge: c.pendingMerge,
      // When the cockpit was deep-linked from a PR, let the server flip that
      // PR's check green once this clears its last blocker. undefined in
      // whole-scope mode → omitted from the body → server skips the flip.
      pr: getReviewPr() ?? undefined,
    }),
  }).then(
    (r) => r.ok,
    () => false
  )
  return res
}

function commit(p: InternalPending): void {
  stack = stack.filter((x) => x.id !== p.id)
  void postSignoff(p.commit).then((ok) => {
    if (!ok) {
      lastError = { key: p.key, commit: p.commit }
      emitError()
    }
  })
  emit()
}

/** Re-POST the failed sign-off and clear the error on success. */
export function retrySignoff(): void {
  const err = lastError
  if (!err) return
  lastError = null
  emitError()
  void postSignoff(err.commit).then((ok) => {
    if (!ok) {
      lastError = err
      emitError()
    }
  })
}

/** Dismiss the surfaced sign-off error without retrying. */
export function clearSignoffError(): void {
  if (!lastError) return
  lastError = null
  emitError()
}

/** Non-reactive read of the surfaced sign-off error (tests / imperative use). */
export function peekSignoffError(): SignoffError | null {
  return lastError
}

/** Subscribe to the most-recent failed sign-off (for an error/retry surface). */
export function useSignoffError(): SignoffError | null {
  return useSyncExternalStore(
    (l) => {
      errorListeners.add(l)
      return () => errorListeners.delete(l)
    },
    () => lastError,
    () => null
  )
}

/** Queue a sign-off behind the 5s undo window. */
export function enqueue(action: PendingCommit): void {
  const p = {
    ...action,
    id: nextId++,
    createdAt: Date.now(),
  } as InternalPending
  p.timer = setTimeout(() => commit(p), UNDO_WINDOW_MS)
  stack = [...stack, p]
  emit()
}

/** Roll back the most recent still-pending action. Returns the reverted item (or null). */
export function undoLast(): QueueItem | null {
  const p = stack[stack.length - 1]
  if (!p) return null
  clearTimeout(p.timer)
  stack = stack.slice(0, -1)
  reviewActions.insertAt(p.revert.index, p.revert.item)
  if (p.kind === "edit") {
    if (p.revert.priorCartItem)
      dispatchCart({ type: "add", item: p.revert.priorCartItem })
    else dispatchCart({ type: "remove", key: p.commit.item.key })
  }
  emit()
  return p.revert.item
}

/** Commit every pending action now (rail unmount / page unload). */
export function flushAll(): void {
  const pending = stack
  stack = []
  pending.forEach((p) => {
    clearTimeout(p.timer)
    void postSignoff(p.commit)
  })
  emit()
}

/** Test-only reset. */
export function resetPending(): void {
  stack.forEach((p) => clearTimeout(p.timer))
  stack = []
  nextId = 1
  lastError = null
  emit()
  emitError()
}

function topSnapshot(): PendingCommit | null {
  return stack.length ? stack[stack.length - 1] : null
}

/** Subscribe to the most-recent pending action (the one the toast shows). */
export function usePending(): PendingCommit | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    topSnapshot,
    () => null
  )
}

/** createdAt for the current top — the toast reads this for its countdown. */
export function pendingCreatedAt(): number | null {
  return stack.length ? stack[stack.length - 1].createdAt : null
}
